import React, { useRef, useState, useCallback, useEffect } from 'react';
import './ProductMentionEditor.css';

/**
 * ProductMentionEditor
 * 
 * Ein Text-Editor, der #Produktname Mentions ermöglicht mit Autocomplete
 * - Nutzer tippen # um ein Produkt zu erwähnen
 * - Autocomplete zeigt Online-Produkte basierend auf der Eingabe
 * - Je präziser die Eingabe, desto präziser die Filterung
 * - Nur Online-Produkte werden vorgeschlagen
 */
export function ProductMentionEditor({ value, onChange, placeholder = 'Schreibe hier... (#Produktname zum erwähnen)', disabled = false }) {
  const textAreaRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState(-1);
  const [selectedSuggestionIdx, setSelectedSuggestionIdx] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Nutzer gibt # ein - starte Mention-Modus
  const handleChange = useCallback(
    (e) => {
      const text = e.target.value;
      onChange(text);

      // Finde die Cursor-Position und prüfe auf #
      const cursorPos = e.target.selectionStart;
      const textBeforeCursor = text.substring(0, cursorPos);
      
      // Suche nach # vor der Cursor-Position
      const lastAtPos = textBeforeCursor.lastIndexOf('#');
      
      if (lastAtPos !== -1) {
        // Prüfe, dass # am Anfang oder nach Space/Newline ist
        const charBeforeAt = lastAtPos > 0 ? text[lastAtPos - 1] : ' ';
        const isValidStartPosition = /[\s\n]/.test(charBeforeAt) || lastAtPos === 0;
        
        if (isValidStartPosition) {
          const queryText = textBeforeCursor.substring(lastAtPos + 1);
          
          // Prüfe, dass der Query nur valide Zeichen hat (keine Zeilenumbrüche)
          if (!queryText.includes('\n') && !queryText.includes('#')) {
            setMentionStartPos(lastAtPos);
            setMentionQuery(queryText);
            
            // Starte die Suche
            if (queryText.length >= 1) {
              searchProducts(queryText);
            } else {
              setSuggestions([]);
              setShowSuggestions(false);
            }
          } else {
            setShowSuggestions(false);
          }
        }
      } else {
        setShowSuggestions(false);
        setMentionStartPos(-1);
      }
    },
    [onChange]
  );

  // Suche nach Produkten mit Query
  const searchProducts = useCallback(async (query) => {
    if (query.length < 1) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`/api/forum/products/search?query=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data || []);
        setShowSuggestions(data && data.length > 0);
        setSelectedSuggestionIdx(0);
      } else {
        console.warn('API error:', response.status);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Fehler beim Suchen nach Produkten:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Nutzer wählt ein Produkt aus der Suggestion-Liste
  const selectProduct = useCallback(
    (product) => {
      if (!textAreaRef.current) return;

      const text = value;
      const beforeMention = text.substring(0, mentionStartPos);
      const afterCursor = text.substring(mentionStartPos + mentionQuery.length + 1);

      // Ersetze #Query mit #ProductName
      const newText = `${beforeMention}#${product.name} ${afterCursor}`;
      onChange(newText);

      // Schliesse die Suggestion-Liste
      setShowSuggestions(false);
      setSuggestions([]);
      setMentionQuery('');
      setMentionStartPos(-1);

      // Setze Cursor nach dem Product Mention
      setTimeout(() => {
        if (textAreaRef.current) {
          const newCursorPos = beforeMention.length + product.name.length + 2; // +2 für "@ "
          textAreaRef.current.focus();
          textAreaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    },
    [value, mentionStartPos, mentionQuery, onChange]
  );

  // Tastatur-Navigation für die Suggestion-Liste
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIdx((prev) => (prev + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIdx((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        break;
      case 'Enter':
        e.preventDefault();
        selectProduct(suggestions[selectedSuggestionIdx]);
        break;
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="product-mention-editor">
      <div className="editor-wrapper">
        <textarea
          ref={textAreaRef}
          className="editor-textarea"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={5}
        />

        {showSuggestions && suggestions.length > 0 && (
          <div className="suggestions-dropdown">
            <div className="suggestions-header">
              <span className="suggestions-label">Produkte (nur Online)</span>
              {isLoading && <span className="suggestions-loading">Wird geladen...</span>}
            </div>
            <ul className="suggestions-list">
              {suggestions.map((product, idx) => (
                <li
                  key={product.id}
                  className={`suggestion-item ${idx === selectedSuggestionIdx ? 'active' : ''}`}
                  onClick={() => selectProduct(product)}
                  onMouseEnter={() => setSelectedSuggestionIdx(idx)}
                >
                  {product.imageUrl && (
                    <img src={product.imageUrl} alt={product.name} className="suggestion-image" />
                  )}
                  <span className="suggestion-name">{product.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductMentionEditor;
